/**
 * BetaGate - Gate component for BETA feature flag
 *
 * When BETA flag is ON:
 * - Authenticated users: Pass through
 * - Unauthenticated users with localStorage flag: Redirect to /login
 * - Unauthenticated users without flag: Redirect to /beta (request form)
 *
 * When BETA flag is OFF:
 * - All users pass through (no gating)
 */

import { type ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlag } from '../contexts/FeatureFlagContext';

interface BetaGateProps {
  children: ReactNode;
}

export const BetaGate = ({ children }: BetaGateProps) => {
  const { user, loading: authLoading } = useAuth();
  const isBetaEnabled = useFeatureFlag('BETA');
  const location = useLocation();

  useEffect(() => {
    console.log('[BetaGate] Gate check', {
      betaEnabled: isBetaEnabled,
      authenticated: !!user,
      path: location.pathname,
      hasRequestFlag: localStorage.getItem('sp_beta_request_submitted') === 'true'
    });
  }, [isBetaEnabled, user, location.pathname]);

  // Wait for auth to load
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto"></div>
          <p className="mt-4 text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  // If BETA flag is OFF, no gating - pass through
  if (!isBetaEnabled) {
    console.log('[BetaGate] BETA OFF - allowing access');
    return <>{children}</>;
  }

  // BETA flag is ON - enforce gating
  console.log('[BetaGate] BETA ON - checking access');

  // Authenticated users pass through
  if (user) {
    console.log('[BetaGate] Authenticated user - allowing access');
    return <>{children}</>;
  }

  // Unauthenticated user - check localStorage flag
  const hasSubmittedRequest = localStorage.getItem('sp_beta_request_submitted') === 'true';

  if (hasSubmittedRequest) {
    console.log('[BetaGate] Request submitted - redirecting to /login');
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: { pathname: location.pathname, search: location.search, hash: location.hash } }}
      />
    );
  }

  // No request submitted - redirect to beta request form
  console.log('[BetaGate] No request - redirecting to /beta');
  return <Navigate to="/beta" replace />;
};
